import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Phone, 
  Clock, 
  CheckCircle2, 
  FileSpreadsheet,
  Zap,
  Flame,
  AlertTriangle,
  Home,
  Award,
  Building2,
  Ruler,
  Droplets,
  FileCheck,
  Star,
  Calendar,
  Euro,
  Crown,
  Castle
} from 'lucide-react';

const DiagnosticAvignon = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const villesAvignon = [
    "Avignon 84000", "Villeneuve-lès-Avignon 30400", "Les Angles 30133",
    "Rochefort-du-Gard 30650", "Saze 30650", "Pujaut 30131",
    "Barbentane 13570", "Rognonas 13870", "Châteaurenard 13160",
    "Noves 13550", "Eyragues 13630", "Saint-Rémy-de-Provence 13210",
    "Maillane 13910", "Graveson 13690", "Cabannes 13440",
    "Plan-d'Orgon 13750", "Sénas 13560", "Mallemort 13370"
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-32 pb-16 px-4 bg-gradient-to-br from-white to-gray-100"
    >
      <div className="container mx-auto max-w-4xl">
        {/* Hero Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <motion.div variants={itemVariants} className="flex justify-center mb-6">
            <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue p-4 rounded-full">
              <MapPin className="h-12 w-12 text-white" />
            </div>
          </motion.div>
          <motion.h1 variants={itemVariants} className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Diagnostic Immobilier Avignon et Région (84000)
          </motion.h1>
          <motion.p variants={itemVariants} className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Expert certifié en diagnostics immobiliers à Avignon, Villeneuve-lès-Avignon, Châteaurenard et dans toute la région. 
            DPE, amiante, électricité, gaz, termites - Intervention rapide dans le 84 et départements limitrophes.
          </motion.p>
        </motion.div>

        {/* Article Content */}
        <motion.article
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="prose prose-lg max-w-none"
        >
          {/* Introduction */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 flex items-center">
              <Award className="h-8 w-8 text-dk-blue mr-4" />
              Diagnostics Immobiliers dans la Région d'Avignon : Votre Expert Certifié
            </h2>
            
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              La région d'<strong>Avignon</strong> est un territoire d'exception où le patrimoine historique côtoie 
              le développement moderne. De la <strong>Cité des Papes</strong> avec son Palais des Papes classé UNESCO 
              à <strong>Villeneuve-lès-Avignon</strong> et sa chartreuse, en passant par <strong>Châteaurenard</strong>, 
              <strong>Saint-Rémy-de-Provence</strong> ou <strong>Barbentane</strong>, chaque transaction immobilière 
              nécessite une expertise pointue en matière de diagnostic.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              <strong>DIAGNOSTI-K</strong>, votre diagnostiqueur immobilier certifié, intervient dans toute la région 
              avignonnaise pour accompagner propriétaires, acquéreurs et professionnels de l'immobilier. Basé à 
              Camaret-sur-Aigues, nous nous déplaçons rapidement sur <strong>Avignon 84000</strong>, 
              <strong>Villeneuve-lès-Avignon 30400</strong>, <strong>Châteaurenard 13160</strong> et dans l'ensemble 
              des communes de la région, qu'elles soient dans le Vaucluse, le Gard ou les Bouches-du-Rhône.
            </p>

            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <CheckCircle2 className="h-6 w-6 text-dk-blue mr-3" />
                Pourquoi choisir DIAGNOSTI-K dans la région d'Avignon ?
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Star className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Expertise patrimoniale</strong> : Spécialiste des bâtiments historiques et classés</span>
                </li>
                <li className="flex items-start">
                  <Clock className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Couverture étendue</strong> : Intervention dans 3 départements (84, 30, 13)</span>
                </li>
                <li className="flex items-start">
                  <FileCheck className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Certifications</strong> : Diagnostiqueur certifié avec toutes les qualifications</span>
                </li>
                <li className="flex items-start">
                  <Euro className="h-5 w-5 text-dk-blue mr-3 mt-1" />
                  <span><strong>Tarifs régionaux</strong> : Prix adaptés à la diversité du territoire</span>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Services détaillés */}
          <motion.div variants={itemVariants} className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-8 flex items-center">
              <FileSpreadsheet className="h-8 w-8 mr-4" />
              Nos Services de Diagnostic dans la Région d'Avignon
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <FileSpreadsheet className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">DPE Avignon et Région</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Le <strong>Diagnostic de Performance Énergétique</strong> est obligatoire pour toute vente ou location 
                  dans la région avignonnaise. Nous réalisons vos DPE à Avignon, Villeneuve-lès-Avignon, 
                  Saint-Rémy-de-Provence et dans toutes les communes environnantes.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Évaluation énergétique adaptée au patrimoine</li>
                  <li>• Classement de A à G</li>
                  <li>• Recommandations respectueuses du bâti ancien</li>
                  <li>• Validité 10 ans</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Zap className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">Diagnostic Électricité Région</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Vérification complète des installations électriques dans les logements de plus de 15 ans 
                  à Châteaurenard, Barbentane, Rognonas, Noves et dans toute la région avignonnaise.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Contrôle de conformité NF C 15-100</li>
                  <li>• Adaptation aux installations anciennes</li>
                  <li>• Sécurité des occupants</li>
                  <li>• Validité 3 ans (vente) / 6 ans (location)</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <Flame className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">Diagnostic Gaz Région</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Contrôle sécuritaire des installations gaz dans les logements équipés d'installations 
                  de plus de 15 ans. Intervention aux Angles, Saze, Pujaut, Eyragues et environs.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Vérification des canalisations</li>
                  <li>• Test d'étanchéité</li>
                  <li>• Contrôle des appareils</li>
                  <li>• Prévention des risques</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-6 w-6 mr-3" />
                  <h3 className="text-xl font-semibold">Diagnostic Amiante Région</h3>
                </div>
                <p className="text-white/90 mb-4">
                  Recherche d'amiante obligatoire dans les bâtiments construits avant 1997. 
                  Expertise spécialisée à Graveson, Maillane, Cabannes, Plan-d'Orgon et alentours.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Repérage des matériaux amiantés</li>
                  <li>• Expertise du patrimoine ancien</li>
                  <li>• Recommandations de gestion</li>
                  <li>• Protection de la santé</li>
                </ul>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">Autres diagnostics disponibles dans la région :</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center">
                  <Home className="h-5 w-5 mr-2" />
                  <span>Termites</span>
                </div>
                <div className="flex items-center">
                  <Droplets className="h-5 w-5 mr-2" />
                  <span>Plomb (CREP)</span>
                </div>
                <div className="flex items-center">
                  <Ruler className="h-5 w-5 mr-2" />
                  <span>Carrez / Boutin</span>
                </div>
                <div className="flex items-center">
                  <FileCheck className="h-5 w-5 mr-2" />
                  <span>ERP</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Zone d'intervention */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 flex items-center">
              <MapPin className="h-8 w-8 text-dk-blue mr-4" />
              Zone d'Intervention dans la Région d'Avignon
            </h2>
            
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              Notre diagnostiqueur certifié intervient dans l'ensemble de la région avignonnaise, couvrant 
              <strong>3 départements</strong> : Vaucluse (84), Gard (30) et Bouches-du-Rhône (13). 
              Que vous soyez à Avignon intra-muros, dans les communes périphériques ou dans les villages 
              environnants, nous nous déplaçons rapidement pour réaliser vos diagnostics immobiliers.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Building2 className="h-6 w-6 text-dk-blue mr-3" />
                  Vaucluse (84)
                </h3>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Avignon 84000</strong> - Cité des Papes</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Home className="h-6 w-6 text-dk-blue mr-3" />
                  Gard (30)
                </h3>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Villeneuve-lès-Avignon 30400</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Les Angles 30133</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Rochefort-du-Gard 30650</strong></span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Castle className="h-6 w-6 text-dk-blue mr-3" />
                  Bouches-du-Rhône (13)
                </h3>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Châteaurenard 13160</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Saint-Rémy-de-Provence 13210</strong></span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="h-4 w-4 text-dk-blue mr-2" />
                    <span><strong>Barbentane 13570</strong></span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Toutes nos communes d'intervention dans la région :</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                {villesAvignon.map((ville, index) => (
                  <div key={index} className="flex items-center">
                    <MapPin className="h-4 w-4 text-dk-blue mr-2 flex-shrink-0" />
                    <span className="font-medium">{ville}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Spécificités locales */}
          <motion.div variants={itemVariants} className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Award className="h-8 w-8 mr-4" />
              Spécificités du Marché Immobilier de la Région Avignonnaise
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white/10 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <Crown className="h-6 w-6 mr-3" />
                  Patrimoine Historique Exceptionnel
                </h3>
                <p className="text-white/90 mb-4">
                  La région d'<strong>Avignon</strong> compte de nombreux monuments historiques et bâtiments classés. 
                  Le <strong>Palais des Papes</strong>, la <strong>Chartreuse de Villeneuve-lès-Avignon</strong> et 
                  les centres anciens nécessitent une expertise particulière.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Diagnostic respectueux du patrimoine</li>
                  <li>• Expertise des bâtiments classés</li>
                  <li>• Connaissance des contraintes ABF</li>
                </ul>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <Building2 className="h-6 w-6 mr-3" />
                  Diversité Architecturale
                </h3>
                <p className="text-white/90 mb-4">
                  De l'architecture médiévale d'<strong>Avignon</strong> aux mas provençaux de 
                  <strong>Saint-Rémy-de-Provence</strong>, en passant par les bastides de 
                  <strong>Châteaurenard</strong>, chaque type de bâtiment a ses spécificités.
                </p>
                <ul className="text-sm text-white/80 space-y-1">
                  <li>• Adaptation aux constructions anciennes</li>
                  <li>• Expertise des mas provençaux</li>
                  <li>• Diagnostics spécialisés par époque</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Processus et délais */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 flex items-center">
              <Clock className="h-8 w-8 text-dk-blue mr-4" />
              Notre Processus dans la Région d'Avignon
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Contact</h3>
                <p className="text-gray-600 text-sm">
                  Appelez-nous ou demandez un devis en ligne. Réponse rapide pour toute la région.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Intervention</h3>
                <p className="text-gray-600 text-sm">
                  Déplacement sous 48h à Avignon, Villeneuve ou dans votre commune.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Rapport</h3>
                <p className="text-gray-600 text-sm">
                  Remise des diagnostics sous 72h maximum par email et courrier.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="h-6 w-6 text-dk-blue mr-3" />
                Horaires d'Intervention dans la Région
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="font-medium text-gray-900">Lundi - Vendredi</p>
                  <p className="text-gray-700">8h00 - 19h00</p>
                  <p className="text-sm text-gray-600">Interventions sur rendez-vous</p>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Samedi</p>
                  <p className="text-gray-700">Sur rendez-vous</p>
                  <p className="text-sm text-gray-600">Selon disponibilités</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div variants={itemVariants} className="bg-gradient-to-r from-dk-blue to-dk-light-blue text-white rounded-xl shadow-lg p-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Besoin d'un Diagnostic dans la Région d'Avignon ?</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Contactez DIAGNOSTI-K pour tous vos diagnostics immobiliers à Avignon, Villeneuve-lès-Avignon, 
              Châteaurenard, Saint-Rémy-de-Provence et dans toute la région. Intervention rapide et expertise patrimoniale.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a 
                href="tel:0789307500"
                className="btn bg-white text-dk-blue hover:bg-gray-100 text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="h-5 w-5 mr-2" />
                07 89 30 75 00
              </a>
              <Link
                to="/contact"
                className="btn bg-white/10 hover:bg-white/20 text-white border-2 border-white text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                Demander un devis
              </Link>
            </div>
          </motion.div>
        </motion.article>
      </div>
    </motion.div>
  );
};

export default DiagnosticAvignon;